import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useWS } from "@/context/WebSocketContext";
import { convMeta } from "@/pages/ChatApp";
import Avatar from "@/components/chat/Avatar";
import MessageList from "@/components/chat/MessageList";
import Composer from "@/components/chat/Composer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Search, MoreVertical, Trash2, Timer, UserPlus, X, ChevronUp, ChevronDown, Check, Building2, Pencil, Users, LogOut, CheckSquare, CheckCheck,
} from "lucide-react";
import { toast } from "sonner";

const TTL_OPTIONS = [
  { label: "Disattivati", value: null },
  { label: "24 ore", value: 86400 },
  { label: "7 giorni", value: 604800 },
  { label: "30 giorni", value: 2592000 },
];

export default function ConversationView({ me, conversation, online, isMobile, onBack, onDelete, onLeave, users, onConversationChanged, initialSearch, chatScale }) {
  const { subscribe, sendTyping } = useWS();
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchOpen, setSearchOpen] = useState(!!initialSearch);
  const [query, setQuery] = useState(initialSearch || "");
  const [matchIds, setMatchIds] = useState([]);
  const [matchIdx, setMatchIdx] = useState(0);
  const [typingNames, setTypingNames] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [pendingTtl, setPendingTtl] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const typingTimers = useRef({});
  const convId = conversation?.id;

  const markRead = useCallback(async () => {
    if (!convId) return;
    try { await api.post(`/conversations/${convId}/read`); } catch {}
  }, [convId]);

  const appendMsg = useCallback((msg) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  // initial load
  useEffect(() => {
    if (!convId) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${convId}/messages`, { params: { limit: 50 } });
        if (!active) return;
        setMessages(data.messages);
        setHasMore(data.hasMore);
        markRead();
      } catch (e) {
        toast.error("Impossibile caricare i messaggi");
      }
    })();
    return () => { active = false; };
  }, [convId, markRead]);

  // WS events for this conversation
  useEffect(() => {
    if (!convId) return;
    const unsub = subscribe((evt) => {
      if (evt.type === "message" && evt.message.conversationId === convId) {
        appendMsg(evt.message);
        if (evt.message.senderId !== me.id) markRead();
      } else if (evt.type === "read" && evt.conversationId === convId && evt.userId !== me.id) {
        setMessages((prev) => prev.map((m) =>
          m.senderId === me.id && !(m.readBy || []).includes(evt.userId)
            ? { ...m, readBy: [...(m.readBy || []), evt.userId] }
            : m));
      } else if (evt.type === "message_deleted" && evt.conversationId === convId) {
        setMessages((prev) => prev.map((m) => (m.id === evt.messageId ? { ...m, deletedForAll: true, text: "", attachment: null } : m)));
      } else if (evt.type === "message_edited" && evt.conversationId === convId) {
        setMessages((prev) => prev.map((m) => (m.id === evt.messageId ? { ...m, text: evt.text, editedAt: evt.editedAt } : m)));
      } else if (evt.type === "reaction" && evt.conversationId === convId) {
        setMessages((prev) => prev.map((m) => (m.id === evt.messageId ? { ...m, reactions: evt.reactions } : m)));
      } else if (evt.type === "typing" && evt.conversationId === convId && evt.userId !== me.id) {
        if (evt.isTyping) {
          setTypingNames((p) => ({ ...p, [evt.userId]: evt.displayName }));
          clearTimeout(typingTimers.current[evt.userId]);
          typingTimers.current[evt.userId] = setTimeout(() => {
            setTypingNames((p) => { const n = { ...p }; delete n[evt.userId]; return n; });
          }, 4000);
        } else {
          setTypingNames((p) => { const n = { ...p }; delete n[evt.userId]; return n; });
        }
      }
    });
    return unsub;
  }, [convId, subscribe, me.id, appendMsg, markRead]);

  useEffect(() => {
    if (initialSearch) { setSearchOpen(true); setQuery(initialSearch); }
  }, [initialSearch]);


  // search matches
  useEffect(() => {
    if (!query.trim()) { setMatchIds([]); setMatchIdx(0); return; }
    const q = query.toLowerCase();
    const ids = messages.filter((m) => !m.deletedForAll && (m.text || "").toLowerCase().includes(q)).map((m) => m.id);
    setMatchIds(ids);
    setMatchIdx(ids.length ? ids.length - 1 : 0);
  }, [query, messages]);

  const loadMore = useCallback(async () => {
    if (!messages.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const before = messages[0].createdAt;
      const { data } = await api.get(`/conversations/${convId}/messages`, { params: { before, limit: 50 } });
      setMessages((prev) => [...data.messages, ...prev]);
      setHasMore(data.hasMore);
    } finally {
      setLoadingMore(false);
    }
  }, [messages, convId, loadingMore]);

  const handleSend = useCallback(async (text, file, replyTo) => {
    let attachmentId = null;
    try {
      if (file) {
        const fd = new FormData();
        fd.append("conversationId", convId);
        fd.append("file", file);
        const { data } = await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
        attachmentId = data.id;
      }
      const { data: msg } = await api.post("/messages", { conversationId: convId, text, attachmentId, replyTo: replyTo || null });
      appendMsg(msg);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Invio non riuscito");
      throw e;
    }
  }, [convId, appendMsg]);

  const onReply = useCallback((m) => {
    setEditTarget(null);
    const senderName = conversation?.members?.find((x) => x.id === m.senderId)?.displayName || "Utente";
    setReplyTarget({ id: m.id, text: m.text || (m.attachment ? "📎 Allegato" : ""), senderName });
  }, [conversation]);

  const onEdit = useCallback((m) => {
    setReplyTarget(null);
    setEditTarget({ id: m.id, text: m.text || "" });
  }, []);

  const editMessage = useCallback(async (id, text) => {
    try {
      const { data } = await api.patch(`/messages/${id}`, { text });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: data.text, editedAt: data.editedAt } : m)));
      onConversationChanged?.();
      toast.success("Nota modificata");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Modifica non riuscita");
      throw e;
    }
  }, [onConversationChanged]);

  const deleteMessage = useCallback(async (id, scope) => {
    try {
      await api.delete(`/messages/${id}`, { params: { scope } });
      if (scope === "all") {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, deletedForAll: true, text: "", attachment: null } : m)));
      } else {
        setMessages((prev) => prev.filter((m) => m.id !== id));
      }
    } catch (e) {
      toast.error("Eliminazione non riuscita");
    }
  }, []);

  const reactMessage = useCallback(async (id, emoji) => {
    try {
      const { data } = await api.post(`/messages/${id}/react`, { emoji });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, reactions: data.reactions } : m)));
    } catch (e) {
      toast.error("Reazione non riuscita");
    }
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const exitSelect = useCallback(() => { setSelectMode(false); setSelected(new Set()); }, []);

  const deleteSelected = async (scope) => {
    const ids = [...selected];
    for (const id of ids) {
      // eslint-disable-next-line no-await-in-loop
      await deleteMessage(id, scope);
    }
    toast.success(`${ids.length} ${ids.length === 1 ? "nota eliminata" : "note eliminate"}`);
    exitSelect();
  };

  const setDisappearing = async (ttlSeconds) => {
    try {
      await api.patch(`/conversations/${convId}/disappearing`, { ttlSeconds });
      onConversationChanged?.();
      toast.success("Impostazione aggiornata");
    } catch (e) {
      toast.error("Operazione non riuscita");
    }
  };

  const addMember = async (userId) => {
    try {
      await api.post(`/conversations/${convId}/members`, { userId });
      onConversationChanged?.();
      toast.success("Membro aggiunto");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeMember = async (userId) => {
    try {
      await api.delete(`/conversations/${convId}/members/${userId}`);
      onConversationChanged?.();
      toast.success("Membro rimosso");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const renameGroup = async () => {
    const name = groupName.trim();
    if (!name) { toast.error("Inserisci un nome"); return; }
    try {
      await api.patch(`/conversations/${convId}/name`, { name });
      onConversationChanged?.();
      setRenameOpen(false);
      toast.success("Gruppo rinominato");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const leaveGroup = async () => {
    try {
      await api.delete(`/conversations/${convId}/members/${me.id}`);
      setConfirmLeave(false);
      toast.success("Hai lasciato il gruppo");
      onLeave?.(convId);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const gotoMatch = (dir) => {
    if (!matchIds.length) return;
    setMatchIdx((i) => (i + dir + matchIds.length) % matchIds.length);
  };

  if (!conversation) {
    return (
      <div className="flex-1 hidden md:flex flex-col items-center justify-center bg-[hsl(var(--chat-bg))] text-center px-8">
        <div className="h-20 w-20 rounded-3xl bg-sky-500/10 flex items-center justify-center mb-6">
          <Building2 className="h-10 w-10 text-sky-500" />
        </div>
        <h2 className="text-xl font-bold">Le tue schede</h2>
        <p className="text-muted-foreground text-sm mt-2 max-w-xs">
          Seleziona un contatto dall'elenco o creane uno nuovo per aggiungere note.
        </p>
      </div>
    );
  }

  const meta = convMeta(conversation, me.id, online);
  const typingArr = Object.values(typingNames);
  let statusText;
  if (typingArr.length === 1) statusText = `${typingArr[0]} sta aggiungendo una nota...`;
  else if (typingArr.length > 1) statusText = "Più persone stanno aggiornando la scheda...";
  else if (meta.isGroup) statusText = `${conversation.members.map((m) => m.displayName.split(" ")[0]).join(", ")}`;
  else statusText = meta.onlineNow ? "Online" : "Offline";

  const nonMembers = users.filter((u) => u.active && !conversation.members.some((m) => m.id === u.id));
  const disappearingLabel = conversation.disappearingTtl
    ? (TTL_OPTIONS.find((o) => o.value === conversation.disappearingTtl)?.label || null)
    : null;
  const allSelectedMine = selected.size > 0 && [...selected].every((id) => {
    const m = messages.find((x) => x.id === id);
    return m && m.senderId === me.id;
  });
  const selectableIds = messages.filter((m) => !m.deletedForAll).map((m) => m.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelectAll = () => {
    setSelected(() => (allSelected ? new Set() : new Set(selectableIds)));
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col h-full bg-[hsl(var(--chat-bg))] overflow-hidden">
      <header className="px-3 py-2.5 flex items-center gap-2 border-b border-border/80 bg-background/90 backdrop-blur-md z-20">
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-button"><ArrowLeft className="h-5 w-5" /></Button>
        )}
        <Avatar name={meta.title} online={meta.onlineNow} isGroup={meta.isGroup} size={40} showStatus={!meta.isGroup} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate" data-testid="conversation-title">{meta.title}</div>
          <div className={`text-xs truncate ${typingArr.length ? "text-sky-500 italic" : "text-muted-foreground"}`} data-testid="conversation-status">
            {statusText}
          </div>
        </div>
        {disappearingLabel && (
          <span data-testid="disappearing-badge" title={`Note a scomparsa: ${disappearingLabel}`}
            className="flex items-center gap-1 flex-shrink-0 text-[11px] font-semibold text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-1">
            <Timer className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{disappearingLabel}</span>
          </span>
        )}
        <Button variant="ghost" size="icon" onClick={() => setSearchOpen((s) => !s)} data-testid="chat-search-toggle"><Search className="h-5 w-5" /></Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="conversation-menu-button"><MoreVertical className="h-5 w-5" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            {meta.isGroup && (
              <>
                <DropdownMenuItem onClick={() => { setGroupName(conversation.name || ""); setRenameOpen(true); }} data-testid="rename-group-button">
                  <Pencil className="h-4 w-4 mr-2" /> Rinomina gruppo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAddOpen(true)} data-testid="manage-members-button">
                  <UserPlus className="h-4 w-4 mr-2" /> Gestisci membri
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuLabel className="flex items-center gap-2 text-xs font-normal text-muted-foreground pt-2">
              <Timer className="h-3.5 w-3.5" /> Note a scomparsa
            </DropdownMenuLabel>
            {TTL_OPTIONS.map((o) => (
              <DropdownMenuItem key={String(o.value)} onClick={() => (o.value === null ? setDisappearing(null) : setPendingTtl(o))} data-testid={`ttl-${o.value}`}>
                <span className="flex-1">{o.label}</span>
                {(conversation.disappearingTtl ?? null) === o.value && <Check className="h-4 w-4 text-sky-500" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { setSelectMode(true); setSelected(new Set()); }} data-testid="select-messages-button">
              <CheckSquare className="h-4 w-4 mr-2" /> Seleziona ed elimina note
            </DropdownMenuItem>
            {meta.isGroup && (
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirmLeave(true)} data-testid="leave-group-button">
                <LogOut className="h-4 w-4 mr-2" /> Esci dal gruppo
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirmDelete(true)} data-testid="delete-conversation-button">
              <Trash2 className="h-4 w-4 mr-2" /> Elimina scheda
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {searchOpen && (
        <div className="bg-accent/40 border-b border-border p-2 flex items-center gap-2" data-testid="chat-search-bar">
          <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca nelle note..."
            className="h-8 border-transparent bg-background" data-testid="chat-search-input" />
          <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
            {matchIds.length ? `${matchIdx + 1}/${matchIds.length}` : "0/0"}
          </span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => gotoMatch(-1)} disabled={!matchIds.length}><ChevronUp className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => gotoMatch(1)} disabled={!matchIds.length}><ChevronDown className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSearchOpen(false); setQuery(""); }}><X className="h-4 w-4" /></Button>
        </div>
      )}

      <MessageList
        messages={messages}
        me={me}
        members={conversation.members}
        isGroup={meta.isGroup}
        query={query}
        currentMatchId={matchIds[matchIdx]}
        convId={convId}
        onReact={reactMessage}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loadingMore={loadingMore}
        selectMode={selectMode}
        selectedIds={selected}
        onToggleSelect={toggleSelect}
        onReply={onReply}
        onEdit={onEdit}
        textScale={chatScale}
      />

      {selectMode ? (
        <div className="border-t border-border bg-background/95 backdrop-blur-md p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-1.5" data-testid="selection-toolbar">
          <Button variant="ghost" size="sm" className="px-2" onClick={exitSelect} data-testid="selection-cancel">Annulla</Button>
          <Button variant="ghost" size="sm" className="px-2 gap-1.5" onClick={toggleSelectAll} data-testid="select-all-button">
            <CheckCheck className="h-4 w-4" /> {allSelected ? "Nessuna" : "Tutte"}
          </Button>
          <span className="text-xs text-muted-foreground flex-1 text-center" data-testid="selection-count">{selected.size}</span>
          <Button variant="outline" size="sm" className="px-2.5" disabled={selected.size === 0} onClick={() => deleteSelected("me")} data-testid="delete-selected-me">
            Per me
          </Button>
          <Button size="sm" className="px-2.5 bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={selected.size === 0 || !allSelectedMine} onClick={() => setConfirmDeleteAll(true)} data-testid="delete-selected-all">
            Per tutti
          </Button>
        </div>
      ) : (
        <Composer conversationId={convId} onSend={handleSend} replyTarget={replyTarget} onCancelReply={() => setReplyTarget(null)} editTarget={editTarget} onSubmitEdit={editMessage} onCancelEdit={() => setEditTarget(null)} />
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la scheda?</AlertDialogTitle>
            <AlertDialogDescription>
              Tutte le note verranno eliminate definitivamente per tutti i membri. L'operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-conversation"
              onClick={() => { setConfirmDelete(false); onDelete(convId); }}>
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Membri del gruppo</DialogTitle></DialogHeader>
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground px-1">Nel gruppo ({conversation.members.length})</div>
            <div className="max-h-44 overflow-y-auto thin-scroll space-y-1">
              {conversation.members.map((u) => (
                <div key={u.id} data-testid={`member-row-${u.id}`} className="w-full flex items-center gap-3 p-2 rounded-lg">
                  <Avatar name={u.displayName} online={u.online} size={36} showStatus={false} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.displayName}{u.id === me.id ? " (tu)" : ""}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                  {u.id !== me.id && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => removeMember(u.id)} data-testid={`remove-member-${u.id}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-1 border-t border-border pt-3">
            <div className="text-xs font-semibold text-muted-foreground px-1 flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Aggiungi membri</div>
            <div className="max-h-44 overflow-y-auto thin-scroll space-y-1">
              {nonMembers.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Tutti gli utenti sono già nel gruppo</p>}
              {nonMembers.map((u) => (
                <button key={u.id} onClick={() => addMember(u.id)} data-testid={`add-member-${u.id}`}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left">
                  <Avatar name={u.displayName} online={u.online} size={36} showStatus={false} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.displayName}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                  <UserPlus className="h-4 w-4 ml-auto text-sky-500" />
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm" data-testid="rename-group-dialog">
          <DialogHeader><DialogTitle>Rinomina gruppo</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
              <Users className="h-5 w-5 text-white" />
            </div>
            <Input autoFocus value={groupName} onChange={(e) => setGroupName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") renameGroup(); }}
              placeholder="Nome del gruppo di lavoro" data-testid="rename-group-input" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenameOpen(false)}>Annulla</Button>
            <Button className="bg-sky-600 hover:bg-sky-700 text-white" onClick={renameGroup} data-testid="rename-group-save">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingTtl} onOpenChange={(o) => !o && setPendingTtl(null)}>
        <AlertDialogContent data-testid="disappearing-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Timer className="h-5 w-5 text-amber-600" /> Attivare le note a scomparsa?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingTtl ? `Le nuove note inviate in questa scheda verranno eliminate automaticamente dopo ${pendingTtl.label.toLowerCase()} dall'invio, per tutti i membri. Le note già inviate non saranno modificate.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-disappearing">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); const v = pendingTtl?.value; setPendingTtl(null); setDisappearing(v); }} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="confirm-disappearing">
              Attiva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmLeave} onOpenChange={setConfirmLeave}>
        <AlertDialogContent data-testid="leave-group-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Uscire dal gruppo?</AlertDialogTitle>
            <AlertDialogDescription>
              Non riceverai più le note di "{meta.title}" e la scheda sparirà dal tuo elenco. Gli altri membri potranno reinvitarti in seguito.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-leave-group">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); leaveGroup(); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-leave-group">
              Esci dal gruppo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}>
        <AlertDialogContent data-testid="delete-all-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare per tutti?</AlertDialogTitle>
            <AlertDialogDescription>
              {selected.size === 1
                ? "Questa nota verrà eliminata definitivamente per tutti i membri della scheda. L'operazione non è reversibile."
                : `${selected.size} note verranno eliminate definitivamente per tutti i membri della scheda. L'operazione non è reversibile.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-all">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); setConfirmDeleteAll(false); deleteSelected("all"); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-all">
              Elimina {selected.size > 1 ? `(${selected.size})` : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
